import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as api from "api/api";
import { BillingCardResource, BillingInvoiceResource, BillingResource } from "api/resource/billing";
import { loginReset } from "redux/slice/authentication";

export interface BillingCardUpdateError {
	status?: number;
	errorCode?: string;
}

export interface BillingState {
	loading?: boolean;
	error?: string | null;
	summary?: BillingResource;
	invoicesLoading?: boolean;
	invoicesError?: string | null;
	invoices?: BillingInvoiceResource[];
}

const initialState: BillingState = {
	loading: false,
	invoicesLoading: false,
};

export const fetchBilling = createAsyncThunk("billing/fetch", async (_, thunkAPI) => {
	thunkAPI.dispatch(billingLoading());
	const result = api.getBilling();
	result
		.then((summary) => thunkAPI.dispatch(billingLoaded(summary)))
		.catch((error) => thunkAPI.dispatch(billingFailed(error)));
	return result;
});

export const fetchBillingInvoices = createAsyncThunk("billing/fetchInvoices", async (_, thunkAPI) => {
	thunkAPI.dispatch(billingInvoicesLoading());
	const result = api.getBillingInvoices();
	result
		.then((invoices) => thunkAPI.dispatch(billingInvoicesLoaded(invoices)))
		.catch((error) => thunkAPI.dispatch(billingInvoicesFailed(error)));
	return result;
});

export const updateBillingCard = createAsyncThunk<
	BillingCardResource,
	{ token: string },
	{ rejectValue: BillingCardUpdateError }
>("billing/updateCard", async (payload, thunkAPI) => {
	return api.updateBillingCard(payload.token)
		.then((card) => {
			thunkAPI.dispatch(billingCardUpdated(card));
			return card;
		})
		.catch((error) => {
			// Default serialization would keep `code` but drop `status`, which the copy needs for
			// 409/5xx responses that carry no errorCode.
			return thunkAPI.rejectWithValue({ status: error && error.status, errorCode: error && error.code });
		});
});

export const billingSlice = createSlice({
	name: "billing",
	initialState,
	reducers: {
		billingLoading: (state) => {
			state.loading = true;
		},
		billingLoaded: (state, action: PayloadAction<BillingResource>) => {
			state.loading = false;
			state.error = null;
			state.summary = action.payload;
		},
		// The summary already loaded stays in place, so a failed refresh doesn't blank the tab.
		billingFailed: (state, action: PayloadAction<{ message?: string } | undefined>) => {
			state.loading = false;
			state.error = action.payload?.message ?? "Unable to load billing";
		},
		billingCardUpdated: (state, action: PayloadAction<BillingCardResource>) => {
			if (state.summary) {
				state.summary.card = action.payload;
			}
		},
		billingInvoicesLoading: (state) => {
			state.invoicesLoading = true;
		},
		billingInvoicesLoaded: (state, action: PayloadAction<BillingInvoiceResource[]>) => {
			state.invoicesLoading = false;
			state.invoicesError = null;
			state.invoices = action.payload;
		},
		billingInvoicesFailed: (state, action: PayloadAction<{ message?: string } | undefined>) => {
			state.invoicesLoading = false;
			state.invoicesError = action.payload?.message ?? "Unable to load invoices";
		},
	},
	extraReducers(builder) {
		builder.addCase(loginReset, () => {
			return initialState;
		});
	},
});

export const {
	billingLoading,
	billingLoaded,
	billingFailed,
	billingCardUpdated,
	billingInvoicesLoading,
	billingInvoicesLoaded,
	billingInvoicesFailed,
} = billingSlice.actions;
